import React, { useMemo } from 'react';
import {
  Box,
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { ExtendedAnalysisResponse } from './utils/statusMappingUtils';
import { DbBatchProcessResponse } from '../../services/module4Service';

interface BatchJobProgressBarProps {
  status: DbBatchProcessResponse | ExtendedAnalysisResponse;
  className?: string;
}

interface ProcessingStats {
  progressPercentage: number;
  totalRecords: number;
  processedRecords: number;
  errorRecords: number;
  processingRate: number; // records per second
  estimatedSecondsRemaining: number;
  estimatedCompletionTime: Date | null;
  elapsedSeconds: number;
}

const BatchJobProgressBar: React.FC<BatchJobProgressBarProps> = ({
  status,
  className
}) => {
  
  // Helper function to format UTC timestamp to local time
  const formatToLocalTime = (timestamp: string): string => {
    if (!timestamp) return 'Not available';
    
    try {
      // API returns UTC timestamps without 'Z' suffix, so we add it to ensure UTC parsing
      const utcTimestamp = timestamp.endsWith('Z') ? timestamp : timestamp + 'Z';
      const date = new Date(utcTimestamp);
      
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      // Format to local time with short timezone name
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      return 'Date error';
    }
  };
  
  const stats: ProcessingStats = useMemo(() => {
    // Use new BatchJob fields with fallback to legacy fields
    const totalRecords = status.total_records || status.total_items || 0;
    const processedRecords = status.processed_records || status.processed_items || 0;
    const errorRecords = status.error_records || status.error_count || 0;
    const progressPercentage = status.progress_percentage || 
      (totalRecords > 0 ? Math.round((processedRecords / totalRecords) * 100) : 0);
    
    // Calculate processing rate and time estimates
    const now = new Date();
    const startedAt = status.started_at ? new Date(status.started_at) : null;
    const lastUpdatedAt = status.batch_job_updated_at ? new Date(status.batch_job_updated_at) : 
                          (status.updated_at ? new Date(status.updated_at) : null);
    
    let processingRate = 0;
    let estimatedSecondsRemaining = 0;
    let estimatedCompletionTime: Date | null = null;
    let elapsedSeconds = 0;
    
    if (startedAt && lastUpdatedAt && processedRecords > 0) {
      elapsedSeconds = (lastUpdatedAt.getTime() - startedAt.getTime()) / 1000;
      processingRate = elapsedSeconds > 0 ? processedRecords / elapsedSeconds : 0;
      
      const remainingRecords = totalRecords - processedRecords;
      if (processingRate > 0 && remainingRecords > 0) {
        estimatedSecondsRemaining = remainingRecords / processingRate;
        estimatedCompletionTime = new Date(now.getTime() + (estimatedSecondsRemaining * 1000));
      }
    }
    
    return {
      progressPercentage,
      totalRecords,
      processedRecords,
      errorRecords,
      processingRate,
      estimatedSecondsRemaining,
      estimatedCompletionTime,
      elapsedSeconds
    };
  }, [status]);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString(undefined, { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const getProgressColor = (): 'primary' | 'error' | 'warning' => {
    if (stats.errorRecords > 0 && stats.processedRecords > 0) {
      const errorRate = (stats.errorRecords / (stats.processedRecords + stats.errorRecords)) * 100;
      if (errorRate > 10) return 'error';
      if (errorRate > 5) return 'warning';
    }
    return 'primary';
  };

  const isCompleted = status.status === 'completed' || status.status === 'finalized';
  const isFailed = status.status === 'failed';
  const isProcessing = status.status === 'processing';

  return (
    <Card className={className} elevation={2} sx={{ mb: 2 }}>
      <CardContent>
        {/* Header with title */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" component="h3">
            Batch Processing Progress
          </Typography>
        </Box>

        {/* Job Details Section */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary">
                Job ID
              </Typography>
              <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                {status.id || status.job_id}
              </Typography>
            </Grid>
            {status.started_at && (
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Started
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatToLocalTime(status.started_at)}
                </Typography>
              </Grid>
            )}
            {(status.batch_job_updated_at || status.updated_at) && (
              <Grid item xs={12} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {formatToLocalTime(status.batch_job_updated_at || status.updated_at)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Main progress bar */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Progress
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {stats.progressPercentage}%
            </Typography>
          </Box>
          <Box sx={{ position: 'relative' }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(stats.progressPercentage, 100)}
              color={getProgressColor()}
              sx={{ height: 12, borderRadius: 6 }} // Thicker main bar
            />
            
            {/* Overlapping animated progress bar at the bottom */}
            {isProcessing && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  mx: 0.5,
                  borderRadius: 1,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-50%',
                    width: '50%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, #ff9800, transparent)',
                    filter: 'blur(0.5px)',
                    boxShadow: '0 0 4px rgba(255, 152, 0, 0.4)',
                    animation: 'scanLeft 3s linear infinite',
                  },
                  '@keyframes scanLeft': {
                    '0%': {
                      left: '-50%',
                    },
                    '100%': {
                      left: '100%',
                    },
                  },
                }}
              />
            )}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {stats.processedRecords.toLocaleString()} of {stats.totalRecords.toLocaleString()} records
            </Typography>
            {isProcessing && stats.estimatedCompletionTime && (
              <Typography variant="body2" color="text.secondary">
                ETA: {formatTime(stats.estimatedCompletionTime)}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Statistics grid */}
        <Grid container spacing={2}>
          {/* Processing Rate */}
          {isProcessing && stats.processingRate > 0 && (
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SpeedIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Rate
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {stats.processingRate.toFixed(1)} rec/s
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Time Elapsed */}
          {stats.elapsedSeconds > 0 && (
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon color="primary" fontSize="small" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Elapsed
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDuration(stats.elapsedSeconds)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Error Count */}
          {stats.errorRecords > 0 && (
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ErrorIcon color="error" fontSize="small" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Errors
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" color="error.main">
                    {stats.errorRecords.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {/* Success Count */}
          {isCompleted && (
            <Grid item xs={6} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                  <Typography variant="body2" fontWeight="medium" color="success.main">
                    {stats.processedRecords.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Status chips */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={status.status?.toUpperCase() || 'UNKNOWN'} 
            color={isCompleted ? 'success' : isFailed ? 'error' : isProcessing ? 'primary' : 'default'}
            size="small"
          />
          
          {isProcessing && stats.estimatedSecondsRemaining > 0 && (
            <Chip 
              label={`${formatDuration(stats.estimatedSecondsRemaining)} remaining`}
              variant="outlined"
              size="small"
            />
          )}
          
          {stats.errorRecords > 0 && (
            <Chip 
              label={`${((stats.errorRecords / (stats.processedRecords + stats.errorRecords)) * 100).toFixed(1)}% error rate`}
              color="warning"
              variant="outlined"
              size="small"
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BatchJobProgressBar;
